// =============================================================================
// components/KpiRules/KpiRuleForm.jsx
//
// Formulario profissional organizado em 4 seccoes:
//   1. Identificacao         (Nome, Tecnologia)
//   2. Origem dos Dados      (Tabela + colunas)
//   3. Formulas              (KPI e Volume)
//   4. Configuracao Detecao  (5 campos operacionais com tooltips + preset)
//
// Todos os campos tem icone Info com tooltip rico explicando teoria.
// =============================================================================
import { useState, useEffect } from 'react';
import {
	Box, Grid, TextField, MenuItem, FormControlLabel, Switch,
	Button, Typography, Divider, Alert, CircularProgress, Tooltip,
	IconButton, InputAdornment, Paper, Chip, Stack,
} from '@mui/material';
import SaveIcon              from '@mui/icons-material/Save';
import CloseIcon             from '@mui/icons-material/Close';
import DeleteIcon            from '@mui/icons-material/Delete';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon       from '@mui/icons-material/AutoAwesome';
import StorageIcon           from '@mui/icons-material/Storage';
import FunctionsIcon         from '@mui/icons-material/Functions';
import TuneIcon              from '@mui/icons-material/Tune';
import BadgeIcon             from '@mui/icons-material/Badge';

import {
	TECH_OPTIONS, DIRECTION_OPTIONS, DETECTION_METHOD_OPTIONS,
	SEVERITY_OPTIONS, KPI_PROFILES, TOOLTIPS,
} from '../../constants/kpiConstants';

// ----------------------------------------------------------------------------
// Componente auxiliar: label com Info tooltip
// ----------------------------------------------------------------------------
function LabelWithTooltip({ text, tooltip }) {
	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
			<span>{text}</span>
			<Tooltip
				title={
					<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.78rem', p: 0.5, maxWidth: 360 }}>
						{tooltip}
					</Box>
				}
				placement="top"
				arrow
			>
				<InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
			</Tooltip>
		</Box>
	);
}

// ----------------------------------------------------------------------------
// Card de seccao com cabecalho destacado
// ----------------------------------------------------------------------------
function SectionCard({ icon, title, subtitle, children, action }) {
	return (
		<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
				<Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
					{subtitle && (
						<Typography variant="caption" color="text.secondary">{subtitle}</Typography>
					)}
				</Box>
				{action}
			</Box>
			{children}
		</Paper>
	);
}

// ============================================================================
// Componente principal
// ============================================================================
export default function KpiRuleForm({
	rule, saving, saveError, validation,
	onSave, onCancel, onValidate, onDelete,
}) {
	const [form, setForm] = useState(rule);

	useEffect(function () { setForm(rule); }, [rule]);

	const isNew = form.kpiId === 0;

	// Setters
	const set = function (field) {
		return function (e) {
			const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
			setForm(function (prev) {
				const out = Object.assign({}, prev);
				out[field] = val;
				return out;
			});
		};
	};
	const setNum = function (field) {
		return function (e) {
			setForm(function (prev) {
				const out = Object.assign({}, prev);
				out[field] = Number(e.target.value);
				return out;
			});
		};
	};
	const setBool = function (field) {
		return function (e) {
			setForm(function (prev) {
				const out = Object.assign({}, prev);
				out[field] = e.target.value === 'true';
				return out;
			});
		};
	};

	// Aplicar preset
	const applyPreset = function (presetKey) {
		const p = KPI_PROFILES.find(function (x) { return x.key === presetKey; });
		if (!p) return;
		setForm(function (prev) { return Object.assign({}, prev, p.preset); });
	};

	const handleSave = function () { onSave(form); };
	const handleValidate = function () {
		onValidate({
			sourceTable:     form.sourceTable,
			sourceVendorCol: form.sourceVendorCol,
			sourceSiteCol:   form.sourceSiteCol,
			sourceCellCol:   form.sourceCellCol,
			sourceDateCol:   form.sourceDateCol,
			kpiFormula:      form.kpiFormula,
			volFormula:      form.volFormula,
		});
	};

	// Descricao explicativa do metodo selecionado
	const selectedMethod = DETECTION_METHOD_OPTIONS.find(function (m) {
		return m.value === form.detectionMethod;
	});

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
				<Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
					{isNew ? 'Nova Regra de KPI' : 'Editar — ' + form.kpiName}
				</Typography>
				{!isNew && (
					<Chip
						label={form.active ? 'Ativa' : 'Inativa'}
						size="small"
						color={form.active ? 'success' : 'default'}
						variant={form.active ? 'filled' : 'outlined'}
					/>
				)}
			</Box>

			{saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

			{/* ============================================ */}
			{/* SECCAO 1: Identificacao                     */}
			{/* ============================================ */}
			<SectionCard
				icon={<BadgeIcon />}
				title="Identificacao"
				subtitle="Nome e tecnologia"
			>
				<Grid container spacing={2}>
					<Grid item xs={12} md={8}>
						<TextField
							label="Nome do KPI *"
							value={form.kpiName}
							onChange={set('kpiName')}
							fullWidth
							size="small"
							placeholder="Ex: Voice Drop Rate 4G"
						/>
					</Grid>
					<Grid item xs={12} md={4}>
						<TextField
							select
							label="Tecnologia *"
							value={form.tech}
							onChange={set('tech')}
							fullWidth
							size="small"
						>
							{TECH_OPTIONS.map(function (t) {
								return <MenuItem key={t} value={t}>{t}</MenuItem>;
							})}
						</TextField>
					</Grid>
				</Grid>
			</SectionCard>

			{/* ============================================ */}
			{/* SECCAO 2: Origem dos Dados                  */}
			{/* ============================================ */}
			<SectionCard
				icon={<StorageIcon />}
				title="Origem dos Dados"
				subtitle="Tabela e colunas dos contadores brutos"
			>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							label="Tabela de Contadores (Source_Table) *"
							value={form.sourceTable}
							onChange={set('sourceTable')}
							fullWidth
							size="small"
							placeholder="Ex: VTK_S12_4G_DATA"
							helperText="Nome exato da tabela na base de dados (case-sensitive)"
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<TextField
							label="Coluna Vendor *"
							value={form.sourceVendorCol}
							onChange={set('sourceVendorCol')}
							fullWidth size="small"
							placeholder="VENDOR"
							helperText="Case-sensitive"
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<TextField
							label="Coluna Site *"
							value={form.sourceSiteCol}
							onChange={set('sourceSiteCol')}
							fullWidth size="small"
							placeholder="JV_ID"
							helperText="Case-sensitive"
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<TextField
							label="Coluna Celula *"
							value={form.sourceCellCol}
							onChange={set('sourceCellCol')}
							fullWidth size="small"
							placeholder="MI_MYCOM_ID"
							helperText="Case-sensitive"
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<TextField
							label="Coluna Data *"
							value={form.sourceDateCol}
							onChange={set('sourceDateCol')}
							fullWidth size="small"
							placeholder="DATE"
							helperText="Case-sensitive"
						/>
					</Grid>
				</Grid>
			</SectionCard>

			{/* ============================================ */}
			{/* SECCAO 3: Formulas                          */}
			{/* ============================================ */}
			<SectionCard
				icon={<FunctionsIcon />}
				title="Formulas SQL"
				subtitle="Expressoes T-SQL que calculam o KPI e o volume"
				action={
					<Button
						size="small"
						variant="outlined"
						startIcon={validation.loading ? <CircularProgress size={14} /> : <PlayArrowIcon />}
						onClick={handleValidate}
						disabled={validation.loading || !form.sourceTable}
					>
						{validation.loading ? 'A validar...' : 'Validar'}
					</Button>
				}
			>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							label="Formula do KPI *"
							value={form.kpiFormula}
							onChange={set('kpiFormula')}
							fullWidth size="small" multiline minRows={2} maxRows={5}
							placeholder="100.0 * SUM(ISNULL(num,0)) / NULLIF(SUM(ISNULL(den,0)), 0)"
							helperText="Use SUM() dos contadores. Protege divisoes com NULLIF."
							inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label="Formula de Volume *"
							value={form.volFormula}
							onChange={set('volFormula')}
							fullWidth size="small"
							placeholder="SUM(ISNULL(denominator,0))"
							helperText="O denominador da formula KPI. Usado para filtros estatisticos."
							inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
						/>
					</Grid>

					{validation.result && (
						<Grid item xs={12}>
							{validation.result.isValid ? (
								<Alert severity="success" icon={<CheckCircleIcon />} sx={{ py: 0.5 }}>
									Formula valida.
									{validation.result.sampleKpi != null
										? ' KPI exemplo: ' + validation.result.sampleKpi.toFixed(3)
										: ''}
									{validation.result.sampleVolume != null
										? ' · Volume: ' + Math.round(validation.result.sampleVolume)
										: ''}
								</Alert>
							) : (
								<Alert severity="error" sx={{ py: 0.5 }}>
									{validation.result.errorMessage}
								</Alert>
							)}
						</Grid>
					)}
				</Grid>
			</SectionCard>

			{/* ============================================ */}
			{/* SECCAO 4: Configuracao Detecao              */}
			{/* ============================================ */}
			<SectionCard
				icon={<TuneIcon />}
				title="Configuracao de Detecao"
				subtitle="5 parametros operacionais com defaults inteligentes por tipo de KPI"
				action={
					<Tooltip title="Aplica um perfil pre-configurado ao formulario" placement="top">
						<Box>
							<TextField
								select
								size="small"
								value=""
								onChange={function (e) { applyPreset(e.target.value); }}
								sx={{ minWidth: 220 }}
								SelectProps={{
									displayEmpty: true,
									renderValue: function () {
										return (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<AutoAwesomeIcon sx={{ fontSize: 16 }} />
												<span>Sugerir valores tipicos</span>
											</Box>
										);
									},
								}}
							>
								{KPI_PROFILES.map(function (p) {
									return <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>;
								})}
							</TextField>
						</Box>
					</Tooltip>
				}
			>
				<Grid container spacing={2}>

					{/* Linha 1: Metodo de detecao */}
					<Grid item xs={12}>
						<TextField
							select
							label={<LabelWithTooltip text="Metodo de Detecao *" tooltip={TOOLTIPS.detectionMethod} />}
							value={form.detectionMethod}
							onChange={set('detectionMethod')}
							fullWidth size="small"
						>
							{DETECTION_METHOD_OPTIONS.map(function (o) {
								return <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>;
							})}
						</TextField>
						{selectedMethod && (
							<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
								{selectedMethod.description}
							</Typography>
						)}
					</Grid>

					{/* Linha 2: SLA Threshold + Direction */}
					<Grid item xs={12} md={6}>
						<TextField
							label={<LabelWithTooltip text="SLA Threshold *" tooltip={TOOLTIPS.slaThreshold} />}
							value={form.slaThreshold}
							onChange={setNum('slaThreshold')}
							fullWidth size="small" type="number"
							inputProps={{ step: 0.1 }}
							helperText="Limiar absoluto do KPI (em valor absoluto ou %)"
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<TextField
							select
							label={<LabelWithTooltip text="Direcao *" tooltip={TOOLTIPS.direction} />}
							value={form.direction.toString()}
							onChange={setBool('direction')}
							fullWidth size="small"
						>
							{DIRECTION_OPTIONS.map(function (o) {
								return (
									<MenuItem key={o.value.toString()} value={o.value.toString()}>
										{o.label}
									</MenuItem>
								);
							})}
						</TextField>
					</Grid>

					{/* Linha 3: Min Volume + Use Day Of Week + Severity */}
					<Grid item xs={12} md={4}>
						<TextField
							label={<LabelWithTooltip text="Min Volume (chao)" tooltip={TOOLTIPS.minVolumeAbsolute} />}
							value={form.minVolumeAbsolute}
							onChange={setNum('minVolumeAbsolute')}
							fullWidth size="small" type="number"
							inputProps={{ step: 5, min: 0 }}
							helperText="Chao estatistico, nao volume de producao"
						/>
					</Grid>
					<Grid item xs={12} md={4}>
						<TextField
							select
							label={<LabelWithTooltip text="Dia-da-Semana" tooltip={TOOLTIPS.useDayOfWeek} />}
							value={form.useDayOfWeek.toString()}
							onChange={setBool('useDayOfWeek')}
							fullWidth size="small"
						>
							<MenuItem value="true">Sim (12 semanas, mesmo dia)</MenuItem>
							<MenuItem value="false">Nao (28 dias consecutivos)</MenuItem>
						</TextField>
					</Grid>
					<Grid item xs={12} md={4}>
						<TextField
							select
							label={<LabelWithTooltip text="Severidade" tooltip={TOOLTIPS.severity} />}
							value={form.severity}
							onChange={setNum('severity')}
							fullWidth size="small"
						>
							{SEVERITY_OPTIONS.map(function (s) {
								return (
									<MenuItem key={s.value} value={s.value}>
										<Box component="span" sx={{
											display: 'inline-block', width: 8, height: 8,
											borderRadius: '50%', bgcolor: s.color, mr: 1,
										}} />
										{s.label}
									</MenuItem>
								);
							})}
						</TextField>
					</Grid>

					{/* Active toggle */}
					<Grid item xs={12}>
						<Divider sx={{ my: 1 }} />
						<FormControlLabel
							control={<Switch checked={form.active} onChange={set('active')} />}
							label={<Typography variant="body2">Regra ativa no motor (deteta diariamente)</Typography>}
						/>
					</Grid>
				</Grid>
			</SectionCard>

			{/* ============================================ */}
			{/* Botoes de acao                              */}
			{/* ============================================ */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2 }}>
				<Box>
					{!isNew && (
						<Button
							variant="outlined"
							color="error"
							startIcon={<DeleteIcon />}
							onClick={function () {
								if (window.confirm('Apagar a regra "' + form.kpiName + '"?')) {
									onDelete(form.kpiId);
								}
							}}
							disabled={saving}
						>
							Apagar
						</Button>
					)}
				</Box>
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" startIcon={<CloseIcon />} onClick={onCancel} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
						{saving ? 'A guardar...' : (isNew ? 'Criar Regra' : 'Guardar')}
					</Button>
				</Stack>
			</Box>
		</Box>
	);
}
